/**
 * ToolSelectDialog — the ONE canonical "pick a tool" view.
 *
 * Shared by every surface that opens a tool/connection picker:
 *   • Connections → "Create New"            (SecretsCatalog)
 *   • Workbench → Agents → Tools & Routing   (AgentToolPicker)
 *   • Room drawer → Tools                    (AgentToolPicker)
 *
 * These surfaces DIVERGE after selection (create-credential wizard vs. enable
 * vs. instance-pin) — but the SELECTION view itself must be identical: same
 * tabbed catalog (MCP Tools + Connectors), live search, responsive card grid,
 * and "Most used / Coming soon / credential" badges. Previously each surface
 * hand-rolled its own grid with different filters → inconsistent look AND tool
 * counts. This component is the single source of truth so they stay in lockstep.
 *
 * Contract: the caller passes a flat ``entries`` list (the FULL catalog for that
 * surface — do not pre-filter by authRequired) where each entry is:
 *   { category: 'mcp'|'connector', name, displayName, description,
 *     authRequired?, comingSoon?, mostUsed?, ...opaque routing fields }
 * and an ``onPick(entry)`` that owns the post-selection divergence. Coming-soon
 * entries render disabled. ``testidPrefix`` keeps each mount's existing
 * data-testids stable.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X as XIcon } from 'lucide-react';

function ToolGlyph({ entry }) {
  const ch = String(entry.displayName || entry.name || '?').trim().charAt(0).toUpperCase() || '?';
  const mcp = entry.category === 'mcp';
  return (
    <div
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold ${
        mcp ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'
      }`}
      aria-hidden
    >
      {ch}
    </div>
  );
}

/** One selectable card in the picker grid. */
function ToolCard({ entry, onPick, testid, disabledReason }) {
  const disabled = Boolean(entry.comingSoon) || Boolean(disabledReason);
  return (
    <button
      type="button"
      disabled={disabled}
      data-testid={testid}
      title={disabledReason || undefined}
      onClick={() => !disabled && onPick(entry)}
      className={`group relative flex h-full flex-col rounded-xl border p-3 text-left transition-all ${
        disabled
          ? 'cursor-not-allowed border-gray-200 bg-gray-50/60 opacity-70'
          : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50/40 hover:shadow-sm'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <ToolGlyph entry={entry} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-gray-900">{entry.displayName}</span>
            {/* Verified trust signal (MCP only): green light = confirmed
                working end-to-end; yellow light = not verified yet. Hover shows
                the meaning. Hints are LLM-generated and were demonstrably wrong
                before grounding, so this tells the user which MCPs are
                trustworthy. */}
            {entry.category === 'mcp' ? (
              entry.verified ? (
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-green-400 ring-2 ring-green-100"
                  title="Verified — this MCP has been confirmed working end-to-end"
                  aria-label="Verified"
                />
              ) : (
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-amber-400 ring-2 ring-amber-100"
                  title="Not verified yet — this MCP has not been confirmed working end-to-end; use with caution"
                  aria-label="Not verified"
                />
              )
            ) : null}
          </div>
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-gray-500">
            {entry.description}
          </p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {entry.mostUsed && !entry.comingSoon ? (
          <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            ★ Most used
          </span>
        ) : null}
        {entry.comingSoon ? (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
            Coming soon
          </span>
        ) : null}
        {entry.category === 'mcp' && entry.authRequired ? (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
            Needs credential
          </span>
        ) : null}
        {entry.category === 'mcp' && !entry.authRequired ? (
          <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
            No credential
          </span>
        ) : null}
        {entry.badge ? (
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${entry.badgeClass || 'bg-gray-100 text-gray-600'}`}>
            {entry.badge}
          </span>
        ) : null}
      </div>
    </button>
  );
}

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {Array}    props.entries              full catalog (mcp + connector)
 * @param {(e)=>void} props.onPick
 * @param {()=>void} props.onCancel
 * @param {string}   [props.title]
 * @param {string}   [props.subtitle]
 * @param {string}   [props.testidPrefix]       keeps each mount's testids stable
 * @param {React.ReactNode} [props.children]    optional sub-view (e.g. MCP instance picker) rendered INSTEAD of the grid
 * @param {string}   [props.headerOverride]     replaces the title when a sub-view is shown
 * @param {(name)=>string|undefined} [props.cardTestId]  per-card testid builder
 * @param {(entry)=>string|undefined} [props.disabledReasonFor]  gray-out + tooltip (e.g. already attached)
 */
export default function ToolSelectDialog({
  open,
  entries = [],
  onPick,
  onCancel,
  title = 'Add a connection',
  subtitle = 'Pick a tool to connect. MCP tools are the modern, recommended path.',
  testidPrefix = 'tool-select',
  dialogTestId = null,
  children = null,
  headerOverride = null,
  cardTestId = null,
  disabledReasonFor = null,
}) {
  const counts = useMemo(() => {
    let mcp = 0;
    let connector = 0;
    for (const t of entries) {
      if (t.category === 'connector') connector += 1;
      else mcp += 1;
    }
    return { mcp, connector };
  }, [entries]);

  // Default to the tab that actually has tools: MCP-first (recommended), but a
  // connector-only mount (no MCP entries) opens straight on Connectors so it
  // isn't an empty grid behind a "0" tab.
  const defaultTab = counts.mcp > 0 ? 'mcp' : 'connector';
  const [tab, setTab] = useState(defaultTab);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setTab(counts.mcp > 0 ? 'mcp' : 'connector');
    setQ('');
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel, counts.mcp]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const inTab = entries.filter((t) => (t.category || 'mcp') === tab);
    const matched = needle
      ? inTab.filter((t) =>
          `${t.displayName} ${t.name} ${t.description}`.toLowerCase().includes(needle))
      : inTab;
    // most-used first, coming-soon last, then alphabetical.
    return [...matched].sort((a, b) => {
      if (Boolean(a.comingSoon) !== Boolean(b.comingSoon)) return a.comingSoon ? 1 : -1;
      if (Boolean(a.mostUsed) !== Boolean(b.mostUsed)) return a.mostUsed ? -1 : 1;
      return String(a.displayName).localeCompare(String(b.displayName));
    });
  }, [entries, tab, q]);

  if (!open || typeof document === 'undefined') return null;

  const TabBtn = ({ id, label, n }) => (
    <button
      type="button"
      onClick={() => { setTab(id); setQ(''); }}
      data-testid={`${testidPrefix}-tab-${id}`}
      className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
        tab === id
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-gray-500 hover:text-gray-800'
      }`}
    >
      {label}
      <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${
        tab === id ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
      }`}>{n}</span>
    </button>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testidPrefix}-title`}
      data-testid={dialogTestId || `${testidPrefix}-dialog`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id={`${testidPrefix}-title`} className="text-base font-semibold text-gray-900">
              {headerOverride || title}
            </h2>
            {!headerOverride ? (
              <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* A sub-view (e.g. MCP instance picker) replaces the catalog grid.
            NOTE: use React.Children.toArray (not a raw truthiness check) — a
            caller passing several ``{cond ? <…/> : null}`` siblings yields an
            ARRAY of nulls when all are inactive, which is truthy and would blank
            the grid. toArray() DROPS null/false children, so this is non-empty
            only when a sub-view is actually rendered. (React.Children.count would
            NOT work — it counts nulls.) */}
        {React.Children.toArray(children).length > 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        ) : (
          <>
            {/* Tabs + search */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5">
              <div className="flex items-center gap-1">
                <TabBtn id="mcp" label="MCP Tools" n={counts.mcp} />
                <TabBtn id="connector" label="Connectors" n={counts.connector} />
              </div>
              <div className="relative my-2">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" aria-hidden />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={`Search ${tab === 'mcp' ? 'MCP tools' : 'connectors'}…`}
                  data-testid={`${testidPrefix}-search`}
                  className="w-56 rounded-md border border-gray-200 py-1.5 pl-7 pr-2 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                />
              </div>
            </div>

            {tab === 'connector' ? (
              <div className="border-b border-amber-100 bg-amber-50/60 px-5 py-2 text-[11px] text-amber-800">
                For external <span className="font-medium">APIs</span>, prefer an MCP tool — REST
                connectors are the fallback when no MCP fits. <span className="font-medium">code-connector</span> is
                different: it checks a repo out on disk for editing &amp; PRs, which MCP doesn’t do.
              </div>
            ) : null}

            {/* Grid */}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {visible.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500">
                  {q.trim()
                    ? `No ${tab === 'mcp' ? 'MCP tools' : 'connectors'} match “${q.trim()}”.`
                    : 'Nothing here yet.'}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  {visible.map((t) => (
                    <ToolCard
                      key={t.name}
                      entry={t}
                      onPick={onPick}
                      testid={cardTestId ? cardTestId(t) : `${testidPrefix}-pick-tool-${t.name}`}
                      disabledReason={disabledReasonFor ? disabledReasonFor(t) : undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
